import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import SellerDashboardLayout from '../../components/SellerDashboardLayout/SellerDashboardLayout';
import { sellersApi, categoriesApi } from '../../services/apiService';
import profileService from '../../services/profileService';
import { useSubscription } from '../../context/SubscriptionContext';
import { useAuth } from '../../context/AuthContext';
import './SupplierProfileInfoPage.css';

const SupplierProfileInfoPage = () => {
  const navigate = useNavigate();
  const { hasPermission, planDisplayName, loading: subscriptionLoading } = useSubscription();
  const { refreshUser } = useAuth();
  // Only check permission after subscription loads
  const canUploadVideos = !subscriptionLoading && hasPermission('canUploadVideos');
  const fileInputRef = useRef(null);
  const brochureInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const snapshotInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const textareaRef = useRef(null);

  // Mode: 'view' or 'edit'
  const [mode, setMode] = useState('view');
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [profileExists, setProfileExists] = useState(false);

  // Password modal state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Profile data state
  const [profileData, setProfileData] = useState({
    // Basic Information
    name: '',
    licenseNumber: '',
    establishYear: '',
    businessType: '',
    numberOfEmployees: '',
    serviceArea: '',
    totalCapitalization: '',
    annualSales: '',
    description: '',
    certificationBadges: [],

    // Location Details
    country: '',
    city: '',
    address: '',
    zipCode: '',

    // Contact Info
    email: '',
    phoneNumber: '',
    websiteLink: '',
    linkedInUrl: '',
    additionalContacts: [{ name: '', designation: '', phone: '' }],

    // Categories
    category: '',
    subCategory: '',

    // Company About/Story
    companyOverview: '',
    companyNotes: [''],

    // Media
    logo: '',
    coverImage: '',
    brochures: [],
    videoUrl: '',
    snapshots: [],

    // Status
    profileStep: 1,
    isProfileComplete: false,
    isVerified: false,
    verificationLevel: 'pending',
    membershipPlan: 'Basic'
  });

  // Step labels - 4 steps only
  const steps = [
    { number: 1, label: 'Info' },
    { number: 2, label: 'File' },
    { number: 3, label: 'About' },
    { number: 4, label: 'Preview' }
  ];

  // Fetch profile data on mount
  useEffect(() => {
    // Check if user is logged in
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!user.id) {
      console.log('No user found, redirecting to login');
      // User not logged in, you can redirect or show message
      setLoading(false);
      return;
    }
    fetchProfileData();
    fetchCategories();
  }, []);

  const fetchProfileData = async () => {
    try {
      setLoading(true);
      // Get user from localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      console.log('Fetching profile for user:', user.id);
      
      if (user.id) {
        const response = await sellersApi.getProfileByUserId(user.id);
        console.log('Profile response:', response);
        
        if (response.exists && response.profile) {
          // Parse JSON fields
          const profile = response.profile;
          
          // Convert null values to empty strings for form fields
          const sanitizedProfile = {};
          Object.keys(profile).forEach(key => {
            sanitizedProfile[key] = profile[key] === null ? '' : profile[key];
          });
          
          setProfileData({
            ...sanitizedProfile,
            certificationBadges: profile.certificationBadges ? 
              (typeof profile.certificationBadges === 'string' ? JSON.parse(profile.certificationBadges) : profile.certificationBadges) : [],
            additionalContacts: profile.additionalContacts ? 
              (typeof profile.additionalContacts === 'string' ? JSON.parse(profile.additionalContacts) : profile.additionalContacts) : [{ name: '', designation: '', phone: '' }],
            companyNotes: profile.companyNotes ? 
              (typeof profile.companyNotes === 'string' ? JSON.parse(profile.companyNotes) : profile.companyNotes) : [''],
            brochures: profile.brochures ? 
              (typeof profile.brochures === 'string' ? JSON.parse(profile.brochures) : profile.brochures) : [],
            snapshots: profile.snapshots ? 
              (typeof profile.snapshots === 'string' ? JSON.parse(profile.snapshots) : profile.snapshots) : [],
            membershipPlan: profile.membershipPlan || 'Basic',
            isVerified: profile.isVerified || false,
            verificationLevel: profile.verificationLevel || 'pending'
          });
          setProfileExists(true);
          setCurrentStep(profile.profileStep || 1);
          
          // Update AuthContext with logo from database for sidebar
          if (profile.logo) {
            refreshUser({
              ...user,
              companyLogo: profile.logo,
              avatar: profile.logo,
              companyName: profile.name || user.companyName
            });
          }
        } else {
          console.log('No profile found, using user data');
          // Pre-fill with user data
          setProfileData(prev => ({
            ...prev,
            name: user.companyName || '',
            email: user.email || '',
            phoneNumber: user.phone || '',
            country: user.country || ''
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await categoriesApi.getAll();
      setCategories(data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setProfileData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAdditionalContactChange = (index, field, value) => {
    const newContacts = [...profileData.additionalContacts];
    newContacts[index] = { ...newContacts[index], [field]: value };
    setProfileData(prev => ({
      ...prev,
      additionalContacts: newContacts
    }));
  };

  const addAdditionalContact = () => {
    setProfileData(prev => ({
      ...prev,
      additionalContacts: [...prev.additionalContacts, { name: '', designation: '', phone: '' }]
    }));
  };

  const removeAdditionalContact = (index) => {
    if (profileData.additionalContacts.length > 1) {
      const newContacts = profileData.additionalContacts.filter((_, i) => i !== index);
      setProfileData(prev => ({
        ...prev,
        additionalContacts: newContacts
      }));
    }
  };

  const handleCompanyNoteChange = (index, value) => {
    const newNotes = [...profileData.companyNotes];
    newNotes[index] = value;
    setProfileData(prev => ({
      ...prev,
      companyNotes: newNotes
    }));
  };

  const addCompanyNote = () => {
    setProfileData(prev => ({
      ...prev,
      companyNotes: [...prev.companyNotes, '']
    }));
  };

  const removeCompanyNote = (index) => {
    if (profileData.companyNotes.length > 1) {
      const newNotes = profileData.companyNotes.filter((_, i) => i !== index);
      setProfileData(prev => ({
        ...prev,
        companyNotes: newNotes
      }));
    }
  };

  const handleFileUpload = (field, file) => {
    // Convert file to base64 data URL for persistent storage
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64Url = reader.result;
      if (field === 'brochures') {
        setProfileData(prev => ({
          ...prev,
          brochures: [...prev.brochures, { name: file.name, url: base64Url }]
        }));
      } else if (field === 'snapshots') {
        setProfileData(prev => ({
          ...prev,
          snapshots: [...prev.snapshots, base64Url]
        }));
      } else {
        setProfileData(prev => ({
          ...prev,
          [field]: base64Url
        }));
      }
    };
    reader.readAsDataURL(file);
  };

  const removeSnapshot = (index) => {
    const newSnapshots = profileData.snapshots.filter((_, i) => i !== index);
    setProfileData(prev => ({
      ...prev,
      snapshots: newSnapshots
    }));
  };

  const removeBrochure = (index) => {
    const newBrochures = profileData.brochures.filter((_, i) => i !== index);
    setProfileData(prev => ({
      ...prev,
      brochures: newBrochures
    }));
  };

  const saveStep = async () => {
    try {
      setSaving(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (!user.id) {
        alert('Please login to save your profile');
        return false;
      }

      // Prepare data for saving
      const dataToSave = {
        ...profileData,
        userId: user.id,
        profileStep: currentStep
      };
      
      // Remove fields that cause issues
      delete dataToSave.products;
      delete dataToSave.user;
      delete dataToSave._count;
      delete dataToSave.createdAt;
      delete dataToSave.updatedAt;
      delete dataToSave.membershipPlan;

      if (profileExists && profileData.id) {
        await sellersApi.updateProfileStep(profileData.id, currentStep, dataToSave);
      } else {
        // For new profile, ensure required fields
        if (!dataToSave.name || dataToSave.name.trim() === '') {
          dataToSave.name = user.companyName || user.email?.split('@')[0] || 'My Company';
        }
        if (!dataToSave.country || dataToSave.country.trim() === '') {
          dataToSave.country = 'UAE';
        }
        const newProfile = await sellersApi.createProfile(dataToSave);
        setProfileData(prev => ({ ...prev, id: newProfile.id }));
        setProfileExists(true);
      }
      return true;
    } catch (error) {
      console.error('Error saving step:', error);
      alert('Failed to save. Please try again. Error: ' + (error.response?.data?.error || error.response?.data?.details || error.message));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    const saved = await saveStep();
    if (saved && currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handlePublish = async () => {
    try {
      setSaving(true);
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      
      if (!user.id) {
        alert('Please login to save your profile');
        return;
      }

      // Prepare data for saving
      const dataToSave = {
        ...profileData,
        userId: user.id,
        profileStep: 4,
        isProfileComplete: true
      };
      
      // Remove fields that cause issues
      delete dataToSave.products;
      delete dataToSave.user;
      delete dataToSave._count;
      delete dataToSave.createdAt;
      delete dataToSave.updatedAt;
      delete dataToSave.membershipPlan;
      
      if (profileExists && profileData.id) {
        const result = await sellersApi.updateProfileStep(profileData.id, 4, dataToSave);
        console.log('Profile updated:', result);
      } else {
        // For new profile, ensure required fields
        if (!dataToSave.name || dataToSave.name.trim() === '') {
          dataToSave.name = user.companyName || user.email?.split('@')[0] || 'My Company';
        }
        if (!dataToSave.country || dataToSave.country.trim() === '') {
          dataToSave.country = 'UAE';
        }
        const result = await sellersApi.createProfile(dataToSave);
        console.log('Profile created:', result);
      }
      
      // Update AuthContext with the new logo
      if (profileData.logo) {
        const updatedUser = {
          ...user,
          avatar: profileData.logo,
          companyLogo: profileData.logo,
          companyName: profileData.name || user.companyName
        };
        // Use refreshUser to update context - it handles localStorage internally
        // For base64 images, we store them in context but may need to truncate for localStorage
        if (profileData.logo.startsWith('data:')) {
          // For base64, just update the context directly (don't store full base64 in localStorage)
          refreshUser({
            ...user,
            companyLogo: profileData.logo,
            avatar: profileData.logo,
            companyName: profileData.name || user.companyName
          });
        } else {
          // For URLs, safe to store in localStorage
          refreshUser(updatedUser);
        }
      }
      
      alert('Profile saved successfully!');
      setMode('view');
      await fetchProfileData();
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save. Please try again. Error: ' + (error.response?.data?.error || error.response?.data?.details || error.message));
    } finally {
      setSaving(false);
    }
  };

  // Text formatting functions for rich editor using document.execCommand
  const applyFormatting = (formatType) => {
    const editor = textareaRef.current;
    if (!editor) return;

    editor.focus();

    switch (formatType) {
      case 'bold':
        document.execCommand('bold', false, null);
        break;
      case 'italic':
        document.execCommand('italic', false, null);
        break;
      case 'underline':
        document.execCommand('underline', false, null);
        break;
      case 'link':
        const url = prompt('Enter URL:');
        if (url) {
          document.execCommand('createLink', false, url);
        }
        break;
      case 'list':
        document.execCommand('insertUnorderedList', false, null);
        break;
      default:
        return;
    }

    // Update state with the new HTML content
    handleInputChange('companyOverview', editor.innerHTML);
  };

  // Handle contenteditable input
  const handleEditorInput = (e) => {
    const content = textareaRef.current ? textareaRef.current.innerHTML : e.target.innerHTML;
    handleInputChange('companyOverview', content);
  };

  // Password change handlers
  const handlePasswordInputChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      return;
    }

    try {
      setChangingPassword(true);
      await profileService.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword,
        passwordData.confirmPassword
      );
      setPasswordSuccess('Password changed successfully!');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess('');
      }, 2000);
    } catch (error) {
      console.error('Error changing password:', error);
      setPasswordError(error.response?.data?.error || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleStepClick = (stepNumber) => {
    if (mode === 'edit') {
      setCurrentStep(stepNumber);
    }
  };

  if (loading) {
    return (
      <SellerDashboardLayout>
        <div className="spi-loading">
          <div className="spi-spinner"></div>
          <p>Loading profile...</p>
        </div>
      </SellerDashboardLayout>
    );
  }

  // Render View Mode
  const renderViewMode = () => {
    console.log('Rendering view mode with profileData:', profileData);
    return (
    <div className="spi-view-mode">
      {/* Basic Information Card */}
      <div className="spi-info-card">
        <h3 className="spi-card-title">Basic Information</h3>
        <div className="spi-info-grid">
          <div className="spi-info-item">
            <span className="spi-info-label">Company Name</span>
            <span className="spi-info-value">{profileData.name || '-'}</span>
          </div>
          <div className="spi-info-item">
            <span className="spi-info-label">License Number</span>
            <span className="spi-info-value">{profileData.licenseNumber || '-'}</span>
          </div>
          <div className="spi-info-item">
            <span className="spi-info-label">Establish Year</span>
            <span className="spi-info-value">{profileData.establishYear || '-'}</span>
          </div>
          <div className="spi-info-item">
            <span className="spi-info-label">Business Type</span>
            <span className="spi-info-value">{profileData.businessType || '-'}</span>
          </div>
          <div className="spi-info-item">
            <span className="spi-info-label">Number of Employees</span>
            <span className="spi-info-value">{profileData.numberOfEmployees || '-'}</span>
          </div>
          <div className="spi-info-item">
            <span className="spi-info-label">Service Area</span>
            <span className="spi-info-value">{profileData.serviceArea || '-'}</span>
          </div>
          <div className="spi-info-item">
            <span className="spi-info-label">Total Capitalization</span>
            <span className="spi-info-value">{profileData.totalCapitalization || '-'}</span>
          </div>
          <div className="spi-info-item">
            <span className="spi-info-label">Annual Sale</span>
            <span className="spi-info-value">{profileData.annualSales || '-'}</span>
          </div>
        </div>
      </div>

      {/* Location Details Card */}
      <div className="spi-info-card">
        <h3 className="spi-card-title">Location Details</h3>
        <div className="spi-info-grid">
          <div className="spi-info-item">
            <span className="spi-info-label">Country</span>
            <span className="spi-info-value">{profileData.country || '-'}</span>
          </div>
          <div className="spi-info-item">
            <span className="spi-info-label">City</span>
            <span className="spi-info-value">{profileData.city || '-'}</span>
          </div>
          <div className="spi-info-item spi-info-item-full">
            <span className="spi-info-label">Address</span>
            <span className="spi-info-value">{profileData.address || '-'}</span>
          </div>
          <div className="spi-info-item">
            <span className="spi-info-label">Zip Code</span>
            <span className="spi-info-value">{profileData.zipCode || '-'}</span>
          </div>
        </div>
      </div>

      {/* Contact Info Details Card */}
      <div className="spi-info-card">
        <h3 className="spi-card-title">Contact Info Details</h3>
        <div className="spi-info-grid">
          <div className="spi-info-item">
            <span className="spi-info-label">Email</span>
            <span className="spi-info-value">{profileData.email || '-'}</span>
          </div>
          <div className="spi-info-item">
            <span className="spi-info-label">Phone Number</span>
            <span className="spi-info-value">{profileData.phoneNumber || '-'}</span>
          </div>
          <div className="spi-info-item spi-info-item-full">
            <span className="spi-info-label">Website Link</span>
            <span className="spi-info-value">{profileData.websiteLink || '-'}</span>
          </div>
        </div>
      </div>

      {/* Company Overview Card */}
      <div className="spi-info-card">
        <h3 className="spi-card-title">Company Overview</h3>
        <p className="spi-overview-text">{profileData.companyOverview || 'No overview added yet.'}</p>
      </div>

      {/* Company Notes Card */}
      <div className="spi-info-card">
        <h3 className="spi-card-title">Company Notes</h3>
        <div className="spi-notes-list">
          {profileData.companyNotes && profileData.companyNotes.length > 0 && profileData.companyNotes[0] ? (
            profileData.companyNotes.map((note, index) => (
              <div key={index} className="spi-note-item">
                <span className="spi-note-bullet">{index + 1}.</span>
                <p className="spi-note-text">{note}</p>
              </div>
            ))
          ) : (
            <p className="spi-empty-text">No notes added yet.</p>
          )}
        </div>
      </div>

      {/* Media Section */}
      <div className="spi-media-section">
        {/* Cover Image */}
        <div className="spi-media-card">
          <h3 className="spi-card-title">Company Cover Image</h3>
          <div className="spi-cover-image-container">
            {profileData.coverImage ? (
              <img src={profileData.coverImage} alt="Cover" className="spi-cover-image" />
            ) : (
              <div className="spi-empty-media">No cover image added</div>
            )}
          </div>
        </div>

        {/* Brochure */}
        <div className="spi-media-card">
          <h3 className="spi-card-title">Company Brochure</h3>
          <div className="spi-brochure-container">
            {profileData.brochures && profileData.brochures.length > 0 ? (
              profileData.brochures.map((brochure, index) => (
                <div key={index} className="spi-brochure-item">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="40" height="40" rx="4" fill="#E8F4FC"/>
                    <path d="M25 12H15C13.9 12 13 12.9 13 14V26C13 27.1 13.9 28 15 28H25C26.1 28 27 27.1 27 26V14C27 12.9 26.1 12 25 12ZM15 14H19V20L17 18.5L15 20V14Z" fill="#29ABE2"/>
                  </svg>
                  <span className="spi-brochure-name">{brochure.name || 'brochure.pdf'}</span>
                </div>
              ))
            ) : (
              <div className="spi-empty-media">No brochure added</div>
            )}
          </div>
        </div>

        {/* Video */}
        <div className="spi-media-card">
          <h3 className="spi-card-title">Company Video</h3>
          <div className="spi-video-container">
            {profileData.videoUrl ? (
              <div className="spi-video-preview">
                <img src={profileData.coverImage || '/placeholder-video.png'} alt="Video thumbnail" className="spi-video-thumbnail" />
                <div className="spi-video-play-btn">
                  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="24" cy="24" r="24" fill="rgba(0,0,0,0.5)"/>
                    <path d="M32 24L20 32V16L32 24Z" fill="white"/>
                  </svg>
                </div>
              </div>
            ) : (
              <div className="spi-empty-media">No video added</div>
            )}
          </div>
        </div>

        {/* Snapshots */}
        <div className="spi-media-card spi-snapshots-card">
          <h3 className="spi-card-title">Company Snapshot</h3>
          <div className="spi-snapshots-grid">
            {profileData.snapshots && profileData.snapshots.length > 0 ? (
              profileData.snapshots.map((img, index) => (
                <div key={index} className="spi-snapshot-item">
                  <img src={img} alt={`Snapshot ${index + 1}`} className="spi-snapshot-image" />
                </div>
              ))
            ) : (
              <div className="spi-empty-media spi-full-width">No snapshots added</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
  };

  // Render Edit Step 1 - Basic Info
  const renderStep1 = () => (
    <div className="spi-edit-step">
      <h3 className="spi-section-title">Basic Information</h3>
      <div className="spi-form-grid">
        <div className="spi-form-group">
          <label className="spi-label">Company Name <span className="spi-required">*</span></label>
          <input
            type="text"
            className="spi-input"
            placeholder="Enter company name"
            value={profileData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
          />
        </div>
        <div className="spi-form-group">
          <label className="spi-label">License Number</label>
          <input
            type="text"
            className="spi-input"
            placeholder="Enter license number"
            value={profileData.licenseNumber}
            onChange={(e) => handleInputChange('licenseNumber', e.target.value)}
          />
        </div>
        <div className="spi-form-group">
          <label className="spi-label">Establish Year</label>
          <input
            type="text"
            className="spi-input"
            placeholder="e.g., 2020"
            value={profileData.establishYear}
            onChange={(e) => handleInputChange('establishYear', e.target.value)}
          />
        </div>
        <div className="spi-form-group">
          <label className="spi-label">Business Type</label>
          <select
            className="spi-select"
            value={profileData.businessType}
            onChange={(e) => handleInputChange('businessType', e.target.value)}
          >
            <option value="">Select business type</option>
            <option value="Manufacturer">Manufacturer</option>
            <option value="Supplier">Supplier</option>
            <option value="Distributor">Distributor</option>
            <option value="Trader">Trader</option>
            <option value="Contractor">Contractor</option>
            <option value="Consultant">Consultant</option>
            <option value="Service Provider">Service Provider</option>
          </select>
        </div>
        <div className="spi-form-group spi-form-group-full">
          <label className="spi-label">Description</label>
          <textarea
            className="spi-textarea"
            placeholder="Enter brief description of your company"
            value={profileData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            rows={3}
          />
        </div>
        <div className="spi-form-group">
          <label className="spi-label">Number of Employees</label>
          <select
            className="spi-select"
            value={profileData.numberOfEmployees}
            onChange={(e) => handleInputChange('numberOfEmployees', e.target.value)}
          >
            <option value="">Select employee count</option>
            <option value="1-10">1-10</option>
            <option value="11-50">11-50</option>
            <option value="51-100">51-100</option>
            <option value="101-500">101-500</option>
            <option value="500+">500+</option>
          </select>
        </div>
        <div className="spi-form-group">
          <label className="spi-label">Certification & Trust Badges</label>
          <input
            type="text"
            className="spi-input"
            placeholder="Add any certification & trust details"
            value={profileData.certificationBadges.join(', ')}
            onChange={(e) => handleInputChange('certificationBadges', e.target.value.split(',').map(s => s.trim()))}
          />
        </div>
      </div>

      <h3 className="spi-section-title">Sales Information</h3>
      <div className="spi-form-grid">
        <div className="spi-form-group">
          <label className="spi-label">Service Area</label>
          <input
            type="text"
            className="spi-input"
            placeholder="e.g., Construction, Interior, Remodel"
            value={profileData.serviceArea}
            onChange={(e) => handleInputChange('serviceArea', e.target.value)}
          />
        </div>
        <div className="spi-form-group">
          <label className="spi-label">Annual Sales</label>
          <input
            type="text"
            className="spi-input"
            placeholder="e.g., $50,000,000"
            value={profileData.annualSales}
            onChange={(e) => handleInputChange('annualSales', e.target.value)}
          />
        </div>
        <div className="spi-form-group">
          <label className="spi-label">Total Capitalization</label>
          <input
            type="text"
            className="spi-input"
            placeholder="e.g., $200,000,000"
            value={profileData.totalCapitalization}
            onChange={(e) => handleInputChange('totalCapitalization', e.target.value)}
          />
        </div>
      </div>

      <h3 className="spi-section-title">Additional Contact Information</h3>
      {profileData.additionalContacts.map((contact, index) => (
        <div key={index} className="spi-contact-row">
          <div className="spi-form-grid spi-form-grid-3">
            <div className="spi-form-group">
              <label className="spi-label">Name</label>
              <input
                type="text"
                className="spi-input"
                placeholder="Contact name"
                value={contact.name}
                onChange={(e) => handleAdditionalContactChange(index, 'name', e.target.value)}
              />
            </div>
            <div className="spi-form-group">
              <label className="spi-label">Designation</label>
              <input
                type="text"
                className="spi-input"
                placeholder="e.g., Sales Manager"
                value={contact.designation}
                onChange={(e) => handleAdditionalContactChange(index, 'designation', e.target.value)}
              />
            </div>
            <div className="spi-form-group">
              <label className="spi-label">Contact No</label>
              <input
                type="text"
                className="spi-input"
                placeholder="+971 XXX XXXX"
                value={contact.phone}
                onChange={(e) => handleAdditionalContactChange(index, 'phone', e.target.value)}
              />
            </div>
          </div>
          {index > 0 && (
            <button className="spi-remove-btn" onClick={() => removeAdditionalContact(index)}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5L15 15" stroke="#FF4444" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          )}
        </div>
      ))}
      <button className="spi-add-btn" onClick={addAdditionalContact}>
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 3V13M3 8H13" stroke="#29ABE2" strokeWidth="2" strokeLinecap="round"/>
        </svg>
        Add More
      </button>

      <h3 className="spi-section-title">Category & Sub-Category</h3>
      <div className="spi-form-grid">
        <div className="spi-form-group">
          <label className="spi-label">Category</label>
          <select
            className="spi-select"
            value={profileData.category}
            onChange={(e) => handleInputChange('category', e.target.value)}
          >
            <option value="">Select category</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.name}>{cat.name}</option>
            ))}
          </select>
        </div>
        <div className="spi-form-group">
          <label className="spi-label">Sub-Category</label>
          <select
            className="spi-select"
            value={profileData.subCategory}
            onChange={(e) => handleInputChange('subCategory', e.target.value)}
          >
            <option value="">Select sub-category</option>
            <option value="General">General</option>
          </select>
        </div>
      </div>

      <h3 className="spi-section-title">Location Details</h3>
      <div className="spi-form-grid">
        <div className="spi-form-group">
          <label className="spi-label">Country <span className="spi-required">*</span></label>
          <select
            className="spi-select"
            value={profileData.country}
            onChange={(e) => handleInputChange('country', e.target.value)}
          >
            <option value="">Select country</option>
            <option value="UAE">United Arab Emirates</option>
            <option value="Saudi Arabia">Saudi Arabia</option>
            <option value="Qatar">Qatar</option>
            <option value="Kuwait">Kuwait</option>
            <option value="Bahrain">Bahrain</option>
            <option value="Oman">Oman</option>
          </select>
        </div>
        <div className="spi-form-group">
          <label className="spi-label">City</label>
          <input
            type="text"
            className="spi-input"
            placeholder="Enter city"
            value={profileData.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
          />
        </div>
        <div className="spi-form-group spi-form-group-full">
          <label className="spi-label">Address</label>
          <input
            type="text"
            className="spi-input"
            placeholder="Enter full address"
            value={profileData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
          />
        </div>
        <div className="spi-form-group">
          <label className="spi-label">Zip Code</label>
          <input
            type="text"
            className="spi-input"
            placeholder="Enter zip code"
            value={profileData.zipCode}
            onChange={(e) => handleInputChange('zipCode', e.target.value)}
          />
        </div>
      </div>

      <h3 className="spi-section-title">Contact Info Details</h3>
      <div className="spi-form-grid">
        <div className="spi-form-group">
          <label className="spi-label">Email <span className="spi-required">*</span></label>
          <input
            type="email"
            className="spi-input"
            placeholder="company@email.com"
            value={profileData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
          />
        </div>
        <div className="spi-form-group">
          <label className="spi-label">Phone Number</label>
          <input
            type="text"
            className="spi-input"
            placeholder="+971 XXX XXXX"
            value={profileData.phoneNumber}
            onChange={(e) => handleInputChange('phoneNumber', e.target.value)}
          />
        </div>
        <div className="spi-form-group">
          <label className="spi-label">Website Link</label>
          <input
            type="url"
            className="spi-input"
            placeholder="www.yourcompany.com"
            value={profileData.websiteLink}
            onChange={(e) => handleInputChange('websiteLink', e.target.value)}
          />
        </div>
        <div className="spi-form-group">
          <label className="spi-label">LinkedIn URL</label>
          <input
            type="url"
            className="spi-input"
            placeholder="LinkedIn profile URL"
            value={profileData.linkedInUrl}
            onChange={(e) => handleInputChange('linkedInUrl', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  // Render Edit Step 2 - Files/Media
  const renderStep2 = () => (
    <div className="spi-edit-step">
      {/* Company Logo Section */}
      <h3 className="spi-section-title">Company Logo</h3>
      <div className="spi-upload-section spi-logo-upload-section">
        <div className="spi-logo-upload-area" onClick={() => fileInputRef.current?.click()}>
          {profileData.logo ? (
            <img src={profileData.logo} alt="Logo" className="spi-logo-preview" />
          ) : (
            <div className="spi-logo-placeholder">
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <rect width="48" height="48" rx="8" fill="#E8F4FC"/>
                <path d="M24 14C20.13 14 17 17.13 17 21C17 24.87 20.13 28 24 28C27.87 28 31 24.87 31 21C31 17.13 27.87 14 24 14ZM24 26C21.24 26 19 23.76 19 21C19 18.24 21.24 16 24 16C26.76 16 29 18.24 29 21C29 23.76 26.76 26 24 26Z" fill="#29ABE2"/>
                <path d="M24 30C19.03 30 15 33.58 15 38V40H33V38C33 33.58 28.97 30 24 30Z" fill="#29ABE2"/>
              </svg>
              <p className="spi-upload-text">Click to upload logo</p>
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => e.target.files[0] && handleFileUpload('logo', e.target.files[0])}
        />
        <p className="spi-file-limit">Recommended: 200x200px (JPG, PNG)</p>
      </div>

      <h3 className="spi-section-title">Company Cover Image</h3>
      <div className="spi-upload-section">
        <div className="spi-file-upload-area" onClick={() => coverInputRef.current?.click()}>
          {profileData.coverImage ? (
            <img src={profileData.coverImage} alt="Cover" className="spi-upload-preview" />
          ) : (
            <>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <path d="M42 30V38C42 39.0609 41.5786 40.0783 40.8284 40.8284C40.0783 41.5786 39.0609 42 38 42H10C8.93913 42 7.92172 41.5786 7.17157 40.8284C6.42143 40.0783 6 39.0609 6 38V30" stroke="#BDBDBD" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M34 16L24 6L14 16" stroke="#BDBDBD" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M24 6V30" stroke="#BDBDBD" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p className="spi-upload-text">Select an image or drag here to upload</p>
              <p className="spi-upload-hint">Max file size: 5MB (JPG, PNG)</p>
            </>
          )}
        </div>
        <input
          ref={coverInputRef}
          type="file"
          accept="image/*"
          style={{ display: 'none' }}
          onChange={(e) => e.target.files[0] && handleFileUpload('coverImage', e.target.files[0])}
        />
      </div>

      <h3 className="spi-section-title">Company Brochure</h3>
      <div className="spi-brochures-list">
        {profileData.brochures.map((brochure, index) => (
          <div key={index} className="spi-brochure-file">
            <div className="spi-brochure-info">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#29ABE2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>{brochure.name || 'brochure.pdf'}</span>
            </div>
            <button className="spi-delete-file" onClick={() => removeBrochure(index)}>
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M15 5L5 15M5 5L15 15" stroke="#FF4444" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        ))}
        <button className="spi-add-file-btn" onClick={() => brochureInputRef.current?.click()}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M8 3V13M3 8H13" stroke="#29ABE2" strokeWidth="2" strokeLinecap="round"/>
          </svg>
          Attach File
        </button>
        <input
          ref={brochureInputRef}
          type="file"
          accept=".pdf,.doc,.docx"
          style={{ display: 'none' }}
          onChange={(e) => e.target.files[0] && handleFileUpload('brochures', e.target.files[0])}
        />
        <p className="spi-file-limit">Max file size: 10MB (PDF, DOC)</p>
      </div>

      <h3 className="spi-section-title">Company Video</h3>
      <div className="spi-upload-section">
        {canUploadVideos ? (
        <div className="spi-file-upload-area spi-video-upload" onClick={() => videoInputRef.current?.click()}>
          {profileData.videoUrl ? (
            <div className="spi-video-preview-wrapper">
              <video src={profileData.videoUrl} className="spi-video-preview" />
              <p>{profileData.videoUrl.substring(0, 30)}...</p>
            </div>
          ) : (
            <>
              <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                <path d="M36 20L48 12V36L36 28V20Z" stroke="#BDBDBD" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                <rect x="2" y="10" width="34" height="28" rx="4" stroke="#BDBDBD" strokeWidth="3"/>
              </svg>
              <p className="spi-upload-text">Upload Company Video</p>
              <p className="spi-upload-hint">Max file size: 100MB (MP4, MOV, AVI)</p>
            </>
          )}
        </div>
        ) : (
        <div className="spi-file-upload-area spi-video-upload spi-disabled" style={{ opacity: 0.6, cursor: 'not-allowed' }}>
          <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
            <path d="M36 20L48 12V36L36 28V20Z" stroke="#BDBDBD" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="2" y="10" width="34" height="28" rx="4" stroke="#BDBDBD" strokeWidth="3"/>
          </svg>
          <p className="spi-upload-text" style={{ color: '#999' }}>Video Upload Not Available</p>
          <p className="spi-upload-hint" style={{ color: '#cc6600' }}>Upgrade to {planDisplayName === 'Basic' ? 'GCC Business' : 'a higher'} plan to upload videos</p>
        </div>
        )}
        <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          style={{ display: 'none' }}
          onChange={(e) => e.target.files[0] && handleFileUpload('videoUrl', e.target.files[0])}
        />
      </div>

      <h3 className="spi-section-title">Company Snapshot</h3>
      <div className="spi-snapshots-upload-grid">
        {profileData.snapshots.map((snapshot, index) => (
          <div key={index} className="spi-snapshot-upload-item">
            <img src={snapshot} alt={`Snapshot ${index + 1}`} className="spi-snapshot-upload-image" />
            <button className="spi-snapshot-remove" onClick={() => removeSnapshot(index)}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M12 4L4 12M4 4L12 12" stroke="white" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        ))}
        {profileData.snapshots.length < 14 && (
          <div className="spi-snapshot-upload-add" onClick={() => snapshotInputRef.current?.click()}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M12 5V19M5 12H19" stroke="#BDBDBD" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </div>
        )}
        <input
          ref={snapshotInputRef}
          type="file"
          accept="image/*"
          multiple
          style={{ display: 'none' }}
          onChange={(e) => {
            if (e.target.files) {
              Array.from(e.target.files).forEach(file => handleFileUpload('snapshots', file));
            }
          }}
        />
      </div>
    </div>
  );

  // Render Edit Step 3 - About Company
  const renderStep3 = () => (
    <div className="spi-edit-step">
      <h3 className="spi-section-title">Write About your company & thoughts</h3>
      <div className="spi-rich-editor">
        <div className="spi-editor-toolbar">
          <button type="button" className="spi-toolbar-btn" onClick={() => applyFormatting('bold')}>Bold</button>
          <button type="button" className="spi-toolbar-btn" onClick={() => applyFormatting('italic')}>Italic</button>
          <button type="button" className="spi-toolbar-btn" onClick={() => applyFormatting('underline')}>Underline</button>
          <button type="button" className="spi-toolbar-btn" onClick={() => applyFormatting('link')}>Link</button>
          <button type="button" className="spi-toolbar-btn" onClick={() => applyFormatting('list')}>List</button>
        </div>
        <div
          ref={(el) => {
            textareaRef.current = el;
            if (el && !el.dataset.initialized) {
              el.innerHTML = profileData.companyOverview || '';
              el.dataset.initialized = 'true';
            }
          }}
          className="spi-editor-textarea spi-editor-contenteditable"
          contentEditable={true}
          onInput={handleEditorInput}
          data-placeholder="Write about your company..."
          suppressContentEditableWarning={true}
        />
      </div>
    </div>
  );

  // Render Edit Step 4 - Preview
  const renderStep4 = () => (
    <div className="spi-edit-step spi-preview-step">
      <p className="spi-preview-note">Review your profile before saving. You can go back to edit any section.</p>
      
      {/* Cover Image Banner */}
      {profileData.coverImage && (
        <div className="spi-preview-cover-banner">
          <img src={profileData.coverImage} alt="Cover" className="spi-preview-cover-banner-img" />
        </div>
      )}
      
      {/* Preview Header Card with Logo */}
      <div className="spi-preview-header-card">
        <div className="spi-preview-company-info">
          <div className="spi-preview-logo-container">
            {profileData.logo ? (
              <img src={profileData.logo} alt="Company Logo" className="spi-preview-logo" />
            ) : (
              <div className="spi-preview-logo-placeholder">
                <svg width="60" height="60" viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect width="60" height="60" rx="8" fill="#E8F4FC"/>
                  <path d="M30 20C25.58 20 22 23.58 22 28C22 32.42 25.58 36 30 36C34.42 36 38 32.42 38 28C38 23.58 34.42 20 30 20ZM30 34C26.69 34 24 31.31 24 28C24 24.69 26.69 22 30 22C33.31 22 36 24.69 36 28C36 31.31 33.31 34 30 34Z" fill="#29ABE2"/>
                  <path d="M30 38C24.48 38 20 41.58 20 46V48H40V46C40 41.58 35.52 38 30 38Z" fill="#29ABE2"/>
                </svg>
              </div>
            )}
          </div>
          <div className="spi-preview-company-details">
            <h2 className="spi-preview-company-name">{profileData.name || 'Company Name'}</h2>
            <p className="spi-preview-company-location">{profileData.city ? `${profileData.city}, ${profileData.country || ''}` : (profileData.country || 'Location')}</p>
          </div>
        </div>
        <div className="spi-preview-header-tabs">
          <button className="spi-preview-tab active">Company</button>
        </div>
      </div>

      {/* Basic Information Card */}
      <div className="spi-preview-section">
        <h3 className="spi-preview-section-title">Basic Information</h3>
        <div className="spi-preview-info-grid">
          <div className="spi-preview-info-item">
            <span className="spi-preview-label">Company Name</span>
            <span className="spi-preview-value">{profileData.name || '-'}</span>
          </div>
          <div className="spi-preview-info-item">
            <span className="spi-preview-label">License Number</span>
            <span className="spi-preview-value">{profileData.licenseNumber || '-'}</span>
          </div>
          <div className="spi-preview-info-item">
            <span className="spi-preview-label">Establish Year</span>
            <span className="spi-preview-value">{profileData.establishYear || '-'}</span>
          </div>
          <div className="spi-preview-info-item">
            <span className="spi-preview-label">Business Type</span>
            <span className="spi-preview-value">{profileData.businessType || '-'}</span>
          </div>
          <div className="spi-preview-info-item">
            <span className="spi-preview-label">Number of Employees</span>
            <span className="spi-preview-value">{profileData.numberOfEmployees || '-'}</span>
          </div>
          <div className="spi-preview-info-item">
            <span className="spi-preview-label">Service Area</span>
            <span className="spi-preview-value">{profileData.serviceArea || '-'}</span>
          </div>
          <div className="spi-preview-info-item">
            <span className="spi-preview-label">Total Capitalization</span>
            <span className="spi-preview-value">{profileData.totalCapitalization || '-'}</span>
          </div>
          <div className="spi-preview-info-item">
            <span className="spi-preview-label">Annual Sale</span>
            <span className="spi-preview-value">{profileData.annualSales || '-'}</span>
          </div>
        </div>
      </div>

      {/* Location Details Card */}
      <div className="spi-preview-section">
        <h3 className="spi-preview-section-title">Location Details</h3>
        <div className="spi-preview-info-grid">
          <div className="spi-preview-info-item">
            <span className="spi-preview-label">Country</span>
            <span className="spi-preview-value">{profileData.country || '-'}</span>
          </div>
          <div className="spi-preview-info-item">
            <span className="spi-preview-label">City</span>
            <span className="spi-preview-value">{profileData.city || '-'}</span>
          </div>
          <div className="spi-preview-info-item full-width">
            <span className="spi-preview-label">Address</span>
            <span className="spi-preview-value">{profileData.address || '-'}</span>
          </div>
          <div className="spi-preview-info-item">
            <span className="spi-preview-label">Zip Code</span>
            <span className="spi-preview-value">{profileData.zipCode || '-'}</span>
          </div>
        </div>
      </div>

      {/* Contact Info Details Card */}
      <div className="spi-preview-section">
        <h3 className="spi-preview-section-title">Contact Info Details</h3>
        <div className="spi-preview-info-grid">
          <div className="spi-preview-info-item">
            <span className="spi-preview-label">Email</span>
            <span className="spi-preview-value">{profileData.email || '-'}</span>
          </div>
          <div className="spi-preview-info-item">
            <span className="spi-preview-label">Phone Number</span>
            <span className="spi-preview-value">{profileData.phoneNumber || '-'}</span>
          </div>
          <div className="spi-preview-info-item full-width">
            <span className="spi-preview-label">Website Link</span>
            <span className="spi-preview-value">{profileData.websiteLink || '-'}</span>
          </div>
        </div>
      </div>

      {/* Company Overview Card */}
      <div className="spi-preview-section">
        <h3 className="spi-preview-section-title">Company Overview</h3>
        <p className="spi-preview-overview-text">{profileData.companyOverview || 'No overview added yet.'}</p>
      </div>

      {/* Categories Card */}
      <div className="spi-preview-section">
        <h3 className="spi-preview-section-title">Categories</h3>
        <div className="spi-preview-categories">
          {profileData.category ? (
            <span className="spi-preview-category-tag">{profileData.category}</span>
          ) : (
            <p className="spi-preview-empty">No categories added yet.</p>
          )}
          {profileData.subCategory && (
            <span className="spi-preview-category-tag">{profileData.subCategory}</span>
          )}
        </div>
      </div>

      {/* Company Notes Card */}
      <div className="spi-preview-section">
        <h3 className="spi-preview-section-title">Company Notes</h3>
        <div className="spi-preview-notes-list">
          {profileData.companyNotes && profileData.companyNotes.length > 0 && profileData.companyNotes[0] ? (
            profileData.companyNotes.map((note, index) => (
              <div key={index} className="spi-preview-note-item">
                <span className="spi-preview-note-bullet">{index + 1}.</span>
                <p className="spi-preview-note-text">{note}</p>
              </div>
            ))
          ) : (
            <p className="spi-preview-empty">No notes added yet.</p>
          )}
        </div>
      </div>

      {/* Company Listings Card */}
      <div className="spi-preview-section">
        <h3 className="spi-preview-section-title">Company Listings</h3>
        
        {/* Company Cover Image */}
        <div className="spi-preview-subsection">
          <h4 className="spi-preview-subsection-title">Company Cover Image</h4>
          <div className="spi-preview-cover-container">
            {profileData.coverImage ? (
              <img src={profileData.coverImage} alt="Cover" className="spi-preview-cover-image" />
            ) : (
              <div className="spi-preview-empty-media">No cover image added</div>
            )}
          </div>
        </div>

        {/* Company Brochure */}
        <div className="spi-preview-subsection">
          <h4 className="spi-preview-subsection-title">Company Brochure</h4>
          <div className="spi-preview-brochure-container">
            {profileData.brochures && profileData.brochures.length > 0 ? (
              profileData.brochures.map((brochure, index) => (
                <div key={index} className="spi-preview-brochure-item">
                  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="40" height="40" rx="4" fill="#E8F4FC"/>
                    <path d="M25 12H15C13.9 12 13 12.9 13 14V26C13 27.1 13.9 28 15 28H25C26.1 28 27 27.1 27 26V14C27 12.9 26.1 12 25 12ZM15 14H19V20L17 18.5L15 20V14Z" fill="#29ABE2"/>
                  </svg>
                  <span className="spi-preview-brochure-name">{brochure.name || 'brochure.pdf'}</span>
                </div>
              ))
            ) : (
              <div className="spi-preview-empty-media">No brochure added</div>
            )}
          </div>
        </div>
      </div>

      {/* Company Snapshots Card */}
      <div className="spi-preview-section">
        <h3 className="spi-preview-section-title">Company Snapshots</h3>
        <div className="spi-preview-snapshots-grid">
          {profileData.snapshots && profileData.snapshots.length > 0 ? (
            profileData.snapshots.map((img, index) => (
              <div key={index} className="spi-preview-snapshot-item">
                <img src={img} alt={`Snapshot ${index + 1}`} className="spi-preview-snapshot-image" />
              </div>
            ))
          ) : (
            <div className="spi-preview-empty-media full-width">No snapshots added</div>
          )}
        </div>
      </div>
    </div>
  );

  // Render current step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderStep1();
      case 2:
        return renderStep2();
      case 3:
        return renderStep3();
      case 4:
        return renderStep4();
      default:
        return renderStep1();
    }
  };

  return (
    <SellerDashboardLayout>
      <div className="spi-content-wrapper">
        {/* Page Header - Title with Edit Button */}
        <div className="spi-header-row">
          {/* Supplier Profile Information Title */}
          <div className="spi-header-item spi-header-title-group">
            <h1 className="spi-page-title">Supplier Profile Information</h1>
          </div>

          {/* Edit Button */}
          {mode === 'view' && (
            <button className="spi-edit-btn" onClick={() => { console.log('Edit button clicked, switching to edit mode'); setMode('edit'); setCurrentStep(1); }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 20H21" stroke="#2B2B2B" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M16.5 3.50023C16.8978 3.1024 17.4374 2.87891 18 2.87891C18.2786 2.87891 18.5544 2.93378 18.8118 3.04038C19.0692 3.14699 19.303 3.30324 19.5 3.50023C19.697 3.69721 19.8532 3.93106 19.9598 4.18843C20.0665 4.4458 20.1213 4.72165 20.1213 5.00023C20.1213 5.2788 20.0665 5.55465 19.9598 5.81202C19.8532 6.06939 19.697 6.30324 19.5 6.50023L7 19.0002L3 20.0002L4 16.0002L16.5 3.50023Z" stroke="#2B2B2B" strokeWidth="1.25" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span>Edit</span>
            </button>
          )}
        </div>

        {/* Info Section - Password, Verification, Membership (Figma Design) */}
        <div className="spi-vertical-info-section">
          {/* Password */}
          <div className="spi-vertical-info-item password-row">
            <span className="spi-vertical-label" style={{fontSize: '20px', lineHeight: '156%'}}>Password</span>
            <button 
              className="spi-change-password-btn"
              onClick={() => setShowPasswordModal(true)}
            >
              Change password
            </button>
          </div>

          {/* Verification Levels */}
          <div className="spi-vertical-info-item">
            <span className="spi-vertical-label">Verification Levels</span>
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px'}}>
              {profileData.isVerified ? (
                <div className="spi-badge spi-verified-badge">
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 11C22 11.9323 20.7329 12.6871 20.5034 13.5472C20.2653 14.4377 20.9782 15.7221 20.528 16.5015C20.0705 17.2923 18.597 17.314 17.9562 17.9562C17.314 18.5984 17.2923 20.0705 16.5014 20.528C15.7221 20.9782 14.4377 20.2653 13.5472 20.5034C12.6871 20.7329 11.9337 22 11 22C10.0663 22 9.31291 20.7329 8.45276 20.5034C7.56231 20.2653 6.27788 20.9782 5.49856 20.528C4.70769 20.0705 4.68605 18.597 4.04382 17.9562C3.4016 17.3154 1.92954 17.2923 1.47205 16.5015C1.02177 15.7221 1.73472 14.4377 1.49659 13.5472C1.26712 12.6871 0 11.9338 0 11C0 10.0663 1.26712 9.3129 1.49659 8.4528C1.73472 7.5623 1.02177 6.2779 1.47205 5.49857C1.92954 4.7077 3.40305 4.68605 4.04382 4.04383C4.68605 3.40161 4.70769 1.92955 5.49856 1.47206C6.27788 1.02178 7.56231 1.73472 8.45276 1.49659C9.31291 1.26712 10.0663 0 11 0C11.9337 0 12.6871 1.26712 13.5472 1.49659C14.4377 1.73472 15.7221 1.02178 16.5014 1.47206C17.2923 1.92955 17.314 3.40305 17.9562 4.04383C18.5984 4.68461 20.0705 4.7077 20.528 5.49857C20.9782 6.2779 20.2653 7.5623 20.5034 8.4528C20.7329 9.3129 22 10.0663 22 11Z" fill="#29ABE2"/>
                    <path d="M16.5876 7.6024C16.1229 7.1377 15.3696 7.1377 14.9049 7.6024L10.0312 12.4761L8.00786 10.4527C7.54315 9.988 6.7898 9.988 6.32509 10.4527C5.86039 10.9174 5.86039 11.6708 6.32509 12.1355L9.14798 14.9584C9.39188 15.2023 9.71372 15.3177 10.0327 15.3062C10.3516 15.3177 10.6734 15.2023 10.9173 14.9584L16.5891 9.2866C17.0538 8.8219 17.0538 8.0686 16.5891 7.6039L16.5876 7.6024Z" fill="white"/>
                  </svg>
                  <span>Verified</span>
                </div>
              ) : (
                <div className="spi-badge spi-pending-badge">
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="11" cy="11" r="10" stroke="#F5A623" strokeWidth="2" fill="none"/>
                    <path d="M11 6V11.5L14 14" stroke="#F5A623" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  <span>Pending</span>
                </div>
              )}
              <span className="spi-upgrade-link">upgrade now</span>
            </div>
          </div>

          {/* Membership Plan */}
          <div className="spi-vertical-info-item">
            <span className="spi-vertical-label">Membership Plan</span>
            <div style={{display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '4px'}}>
              <div className="spi-membership-badge">
                <img 
                  src="https://api.builder.io/api/v1/image/assets/TEMP/d8ee44da49d1e0c28cf5bfe995ca6aa7f62420ea?width=44" 
                  alt="Membership Icon" 
                  className="spi-membership-icon"
                />
                <span>{profileData.membershipPlan || 'Basic'}</span>
              </div>
              <span className="spi-upgrade-link">upgrade now</span>
            </div>
          </div>
        </div>

        {/* Progress Stepper - Only show in edit mode */}
        {mode === 'edit' && (
          <div className="spi-stepper">
            <div className="spi-stepper-track">
              {steps.map((step, index) => (
                <React.Fragment key={step.number}>
                  <div 
                    className={`spi-step ${currentStep >= step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''} ${mode === 'edit' ? 'clickable' : ''}`}
                    onClick={() => handleStepClick(step.number)}
                  >
                    <div className="spi-step-circle">
                      {currentStep > step.number ? (
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path d="M13 4L6 12L3 9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      ) : (
                        <span>{step.number}</span>
                      )}
                    </div>
                    <span className="spi-step-label">{step.label}</span>
                  </div>
                  {index < steps.length - 1 && (
                    <div className={`spi-step-line ${currentStep > step.number ? 'active' : ''}`}></div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        )}

        {/* Content Area */}
        <div className="spi-content-area">
          {mode === 'view' ? renderViewMode() : renderStepContent()}
        </div>

        {/* Action Buttons */}
        {mode === 'edit' && (
          <div className="spi-action-buttons">
            <button 
              className="spi-btn spi-btn-cancel" 
              onClick={() => { setMode('view'); fetchProfileData(); }}
            >
              Cancel
            </button>
            <div className="spi-btn-group">
              {currentStep > 1 && (
                <button className="spi-btn spi-btn-secondary" onClick={handlePrevious}>
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                  Previous
                </button>
              )}
              {currentStep < 4 && (
                <button className="spi-btn spi-btn-primary" onClick={handleNext} disabled={saving}>
                  {saving ? 'Saving...' : 'Next'}
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              )}
              {currentStep === 4 && (
                <button className="spi-btn spi-btn-save" onClick={handlePublish} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="spi-password-modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="spi-password-modal" onClick={(e) => e.stopPropagation()}>
            <div className="spi-password-modal-header">
              <h3>Change Password</h3>
              <button 
                className="spi-password-modal-close"
                onClick={() => {
                  setShowPasswordModal(false);
                  setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  setPasswordError('');
                  setPasswordSuccess('');
                }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M18 6L6 18M6 6L18 18" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <form onSubmit={handleChangePassword} className="spi-password-form">
              {passwordError && (
                <div className="spi-password-error">{passwordError}</div>
              )}
              {passwordSuccess && (
                <div className="spi-password-success">{passwordSuccess}</div>
              )}
              <div className="spi-password-field">
                <label>Current Password</label>
                <input
                  type="password"
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordInputChange}
                  placeholder="Enter current password"
                />
              </div>
              <div className="spi-password-field">
                <label>New Password</label>
                <input
                  type="password"
                  name="newPassword"
                  value={passwordData.newPassword}
                  onChange={handlePasswordInputChange}
                  placeholder="Enter new password"
                />
              </div>
              <div className="spi-password-field">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  name="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordInputChange}
                  placeholder="Confirm new password"
                />
              </div>
              <div className="spi-password-actions">
                <button
                  type="button"
                  className="spi-password-cancel-btn"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
                    setPasswordError('');
                    setPasswordSuccess('');
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="spi-password-submit-btn"
                  disabled={changingPassword}
                >
                  {changingPassword ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </SellerDashboardLayout>
  );
};

export default SupplierProfileInfoPage;
